import { useState, useEffect, useContext } from "react";
import "../page-list/PageList.css";
import { useNavigate } from "react-router-dom";
import { getNameForDuplicate } from "../../utils/utils";
import { SecurityAttributes, LogEventType } from "../../utils/data/enums";
import { useAuthContext } from "../../store/AuthContext";
import { getAutoImports, getAutoImport, updateAutoImport, deleteAutoImport, createAutoImport } from "../../services/importService";
import Toast from "../ui/toasts/Toast";
import Spinner from "../ui/spinners/Spinner";
import { getDateFromZulu } from "../helpers/getDateFromZulu";
import styles from "./ImportQueuePage.module.scss";
import { ModalContext, ModalTypes } from "../../store/ModalContext";
import NotFoundBox from "../ui/no_data_indicators/NotFoundBox";
import { logEvent } from '../../services/loggingService';
import { ROUTES, ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const ImportAutomationListPage = () => {
  const navigate = useNavigate();
  const { hasAttribute } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [rowsClicked, setRowsClicked] = useState([]);
  const { showModal } = useContext(ModalContext);

  //Data Manipulation Data Prep
  var [autoImports, setAutoImports] = useState([]);

  useEffect(() => {
    loadAutoImports();
  }, []);

  const loadAutoImports = async () => {
    setIsLoading(true);
    try {
      const response = await getAutoImports();
      setAutoImports(response);
    } catch (error) {
      console.error("Failed to fetch auto  imports", error);
      Toast.error(getText('msg_08a546a01966'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allAutoImorts = autoImports.map((autoImport) => autoImport.id);
      setRowsClicked(allAutoImorts);
      // Check all checkboxes
      autoImports.forEach((autoImport) => {
        const checkbox = document.getElementById(autoImport.id);
        if (checkbox) checkbox.checked = true;
      });
    } else {
      setRowsClicked([]);
      // Uncheck all checkboxes
      autoImports.forEach((autoImport) => {
        const checkbox = document.getElementById(autoImport.id);
        if (checkbox) checkbox.checked = false;
      });
    }
  };

  const onToggle = async (id) => {

    try {
      var autoImport = await getAutoImport(id);

      if (!autoImport) throw new Error("Auto Import not found");

      await updateAutoImport({ ...autoImport, isActive: !autoImport.isActive });

      Toast.success(
        `Auto Import ${autoImport.isActive ? "disabled" : "enabled"} successfully`
      );

      // make the change on the UI
      autoImports = autoImports.filter((autoImport) => {
        if (autoImport.id === id) {
          autoImport.isActive = !autoImport.isActive
        }
        return autoImport;
      });

      setAutoImports([...autoImports]);
    } catch (error) {
      console.error("Failed to change auto import enabled status", error);
      Toast.error(getText('msg_3d75967e6d7c'));
    }
  };

  const getStatusClass = (isActive) => {
    if (isActive)
      return "ing-badge-active";
    else
      return "ing-badge-inactive";
  };

  const openDeleteConfirmationModal = () => {
    if (!rowsClicked.length) {
      Toast.error(getText('msg_9bd1b9956da8'));
      return;
    }

    showModal({
      type: ModalTypes.DELETE_CONFIRMATION,
      title: getText('msg_b957efeb6067'),
      onYes: () => handleDelete(),
      onNo: () => { },
      noButtonText: getText('msg_77dfd2135f4d'),
      yesButtonText: getText('msg_f6fdbe48dc54'),
    });
    logEvent(LogEventType.DeleteClicked, `Auto import delete clicked`);
  };

  const handleDelete = async () => {
    await handleAutoImportDeletes(rowsClicked);
    setRowsClicked([]);
  };

  const handleAutoImportDeletes = async (autoImportIds) => {
    const successfulDeletions = [];
    const failedDeletions = [];

    for (const autoImportId of autoImportIds) {
      try {
        await deleteAutoImport(autoImportId);
        successfulDeletions.push(autoImportId);
      } catch (error) {
        failedDeletions.push(autoImportId);
        console.error(`Failed to delete auto import ${autoImportId}:`, error);
      }
    }

    if (successfulDeletions.length && failedDeletions.length) {
      Toast.error(getText('msg_05e507814972'));
    }

    if (successfulDeletions.length && !failedDeletions.length) {
      Toast.success(getText('msg_3184ed7a10e2'));
    }

    loadAutoImports();
  };

  const handleDuplicateAutoImports = () => {
    if (!rowsClicked.length) {
      Toast.error(getText('msg_b244a580f588'));
      return;
    }
    duplicateAutoImports(rowsClicked);
    setRowsClicked([]);
  };

  const duplicateAutoImports = async (autoImportIds) => {
    setIsLoading(true);

    for (let autoImportId of autoImportIds) {
      try {
        const originalAutoImport = await getAutoImport(autoImportId);

        const existingNames = autoImports.map((group) => group.name);
        const newName = getNameForDuplicate(
          originalAutoImport.name,
          existingNames
        );

        delete originalAutoImport.id;
        originalAutoImport.name = newName;

        const autoImportCopy = { ...originalAutoImport };
        await createAutoImport(autoImportCopy);
        Toast.success(`Group "${originalAutoImport.name}" copied successfully`);
      } catch (error) {
        Toast.error(
          error?.response?.data?.detail || "Failed to copy auto import"
        );
      }
    }
    loadAutoImports();
  };

  const handleCheckboxChange = (autoImportId, isChecked) => {
    setRowsClicked(
      isChecked
        ? [...rowsClicked, autoImportId]
        : rowsClicked.filter((id) => id !== autoImportId)
    );
  };

  const onEditAutoImport = (autoImportId) => {
    navigate(ROUTE_BUILDERS.autoImportEdit(autoImportId))
  }

  return (isLoading ? (
    <Spinner width={50} className={styles.loadingSpinner} />
  ) : (
    <div className="page-content">
      <div className="card">
        <div className="card-body px-0 py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <p className="page-title float-start card-title ms-4">
              {getText('msg_7502f272b5dd')}
            </p>
            {hasAttribute(
              SecurityAttributes.ImportsAutoImport
            ) && (
                <div className="float-end d-flex align-items-center">
                  <span type="button" className="ing-text-buttons mx-3" onClick={openDeleteConfirmationModal}>
                    <i className="bi bi-trash3 me-2" />
                    {getText('msg_f6fdbe48dc54')}
                  </span>
                  <span
                    type="button"
                    onClick={handleDuplicateAutoImports}
                    className="ing-text-buttons mx-3"
                  >
                    <i className="bi bi-copy me-2" />
                    {getText('msg_972d57379db3')}
                  </span>
                  <span
                    type="button"
                    className="ms-3 me-4 button-base ing-button-primary-left"
                    onClick={() => navigate(ROUTES.AUTO_IMPORTS_EDIT_ADD)}
                  >
                    {getText('msg_61cc55aa0453')}
                  </span>
                </div>
              )}
          </div>
          <div className="table-responsive-xxl w-100">
            {/* Page List Table */}
            <table className="table table-hover align-middle">
              <thead className="custom-table-header table-light">
                <tr>
                  <th className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      onChange={handleSelectAll}
                      checked={
                        rowsClicked.length === autoImports.length &&
                        autoImports.length > 0
                      }
                    />
                  </th>
                  <th>
                    <span>{getText('msg_709a23220f2c')}</span>
                  </th>
                  <th>
                    <span>{getText('msg_bae7d5be7082')}</span>
                  </th>
                  <th>
                    <span>{getText('msg_a3c686e711e4')}</span>
                  </th>
                  <th>
                    <span>{getText('msg_3ec1ae061c27')}</span>
                  </th>
                  <th>
                    <span>{getText('msg_5f0f1a3c010a')}</span>
                  </th>
                  <th className="text-center">
                    <span>{getText('msg_20063ad90532')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {autoImports.map((autoImport, index) => (
                  <tr
                    key={autoImport.id}
                  >
                    <td className="text-center">
                      <input className="form-check-input" type="checkbox" id={autoImport.id}
                        onChange={(e) => handleCheckboxChange(autoImport.id, e.target.checked)
                        } />
                    </td>
                    <td className="item-page-name titleCell"
                      onClick={() => onEditAutoImport(autoImport.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          onEditAutoImport(autoImport.id);
                        }
                      }}
                    >
                      {autoImport.name}
                    </td>
                    <td>
                      <button className={getStatusClass(autoImport.isActive)}>
                        {autoImport.isActive ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="item-page-last-edited">{autoImport.type}</td>
                    <td className="item-page-last-edited">
                      {autoImport.templateName}
                    </td>
                    <td
                      className="item-page-last-edited"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    >
                      {autoImport.lastRunDate ? getDateFromZulu(autoImport.lastRunDate) : ''}
                    </td>
                    <td className="text-center">
                      <div
                        className={`form-switch ${autoImport.isActive !== "Active" && autoImport.isActive !== "Inactive"
                          ? "form-switch-locked"
                          : ""
                          }`}
                      >
                        <input
                          onClick={() => onToggle(autoImport.id)}
                          className="form-check-input custom-switch-input"
                          type="checkbox"
                          checked={autoImport.isActive}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-center fs-3">
              <>
                {autoImports.length === 0 && (
                  <NotFoundBox
                    backgroundColor="#f0f4ff"
                    title={getText('msg_cae98efd53df')}
                  />
                )}
              </>
            </p>
          </div>
        </div>
      </div>
    </div>
  ));
};

export default ImportAutomationListPage;
